import React, { FC, useEffect, useMemo, useState } from 'react';
import { Grid, TGridProps, useMediaQuery } from '@rbx/ui';
import PageBreadcrumbs, { BreadcrumbItem } from '@modules/ui/shared/components/PageBreadcrumbs';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import OpenSourceEditButton from '@modules/openSource/components/OpenSourceEditButton';
import FeedbackButton from '@modules/feedback/components/FeedbackButton';
import { useShareFeedbackProvider } from '@modules/feedback/context/ShareFeedbackProvider';
import { useRobloxAuthentication } from '@rbx/auth';
import LanguageMenu from '@modules/localization/components/LanguageMenu';
import groupsClient from '@modules/clients/groups';
import ExploreCoursesBanner from '@modules/learning/components/ExploreCoursesBanner';
import { apiReferenceBasePath } from '@modules/apiReference/page/constants/apiReferenceConstants';
import useAppMainNavbarStyles from './AppMainNavbar.styles';

export type AppMainNavbarProps = TGridProps & {
  pageTitle: string; // excludes site title suffix
  breadcrumbsItems?: BreadcrumbItem[] | null;
};

// NOTE (tchu, 2025-12-02): This is the old IA. Also update AppHeading.tsx.
const AppMainNavbar: FC<React.PropsWithChildren<AppMainNavbarProps>> = ({
  pageTitle,
  breadcrumbsItems,
  ...props
}) => {
  const { contentDirFilePath, isOpenSourced, machineTranslated } = useAppConfig();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('Medium'));
  const {
    classes: { actionsWithBreadcrumbs },
  } = useAppMainNavbarStyles();
  const hasBreadcrumbs = useMemo(
    () => breadcrumbsItems && breadcrumbsItems.length,
    [breadcrumbsItems],
  );
  const { isFetched: isAuthLoaded } = useRobloxAuthentication();
  const { isReady: isAllowFeedbackCheckReady, isDocsFeedbackAllowed } = useShareFeedbackProvider();

  const [isEmployee, setIsEmployee] = useState(false);
  useEffect(() => {
    groupsClient.isEmployee().then(setIsEmployee);
  }, []);
  const showOpenSourceEditButton =
    isOpenSourced || isEmployee || process.env.environment !== 'production';

  const { enableEngineReferenceRevamp } = useAppConfig();
  const isEngineReferencePage =
    typeof window !== 'undefined' && window.location.pathname.includes(apiReferenceBasePath);
  const useEngineReferenceButtons = (isEngineReferencePage && enableEngineReferenceRevamp) ?? false;

  return (
    <Grid container marginTop={0} alignItems='center' marginBottom={2} {...props}>
      <ExploreCoursesBanner enableNewLayout={false} />
      {hasBreadcrumbs && (
        <Grid item XSmall>
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          <PageBreadcrumbs items={breadcrumbsItems!} />
        </Grid>
      )}
      <Grid item Medium={hasBreadcrumbs ? 'auto' : 16} XSmall={16}>
        <Grid
          container
          justifyContent={isMobile ? 'flex-start' : 'flex-end'}
          className={hasBreadcrumbs ? actionsWithBreadcrumbs : undefined}>
          <LanguageMenu engineRevampEnabled={useEngineReferenceButtons} />
          <FeedbackButton
            pageTitle={pageTitle}
            show={!isAuthLoaded || !isAllowFeedbackCheckReady || isDocsFeedbackAllowed}
            engineRevampEnabled={useEngineReferenceButtons}
          />
          {showOpenSourceEditButton && contentDirFilePath && (
            <Grid item XSmall='auto'>
              <OpenSourceEditButton
                contentDirFilePath={contentDirFilePath}
                isEmployee={isEmployee}
                machineTranslated={machineTranslated}
                engineRevampEnabled={useEngineReferenceButtons}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AppMainNavbar;
